import { ArrayItem, SMap } from '@client/new/helpers/types';
import { UpChunk } from '@mux/upchunk';
import { GetLibrary, GetLibraryContentQuery } from 'generated/graphql';

export type videoSourceType = 's3' | 'mux';

export type LibraryVideo = {
  poster: string;
  videoSrc: string;
  downloadUrl: string;

  name: string;
  id: number | string;
  creatorId: number;
  createdAt: string;
  fileSize: number;
  width: number;
  height: number;
  duration: number;
  description?: string;
};

export type Video = {
  creatorId: number;
  id: string;
  videoSourceType: videoSourceType;
  type: string;

  url: string;
  downloadUrl?: string;
  thumbnailUrl: string;

  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;

  width: number;
  height: number;
  duration: number;
  fileSize: number;
  folderId: number | null;

  isProcessing?: boolean;

  tags?: string[];
};

export type Folder = {
  id: number;
  name: string;
  parentId: number | null;
  updatedAt: string;
};

export type UploadStatus = 'ready' | 'uploading' | 'finished' | 'error';

export type UploadState = {
  progress: number;
  fileName: string;
  fileId: string;
  state: UploadStatus;
  muxInstance: UpChunk;
  folderId: number | null;
};

export type UploadMap = SMap<UploadState>;

export type GQLVideo = ArrayItem<GetLibraryContentQuery['getCreatorsMedia']>;
export type GQLFolder = ArrayItem<GetLibraryContentQuery['getCreatorsFolders']>;

export type Videos = Array<Video>;
export type Folders = Array<Folder>;

const toDownloadUrl = (download: GQLVideo['downloadLink']) =>
  download
    ? download?.high?.link || download?.medium?.link || download?.low?.link
    : undefined;

export const toVideo = (v: GQLVideo): Video => ({
  id: v.uuid,
  creatorId: v.creatorId,
  videoSourceType: v.videoSourceType as videoSourceType,
  type: v.type || '',

  thumbnailUrl: v.videoThumbnailUrl || '',
  url: v.videoUrl || '',
  downloadUrl: v.downloadLink ? toDownloadUrl(v.downloadLink) : undefined,

  name: v.title,
  description: v.description || '',
  createdAt: v.createdAt,
  updatedAt: v.updatedAt,

  width: v.width || 0,
  height: v.height || 0,
  fileSize: v.fileSize || 0,
  duration: v.duration || 0,
  folderId: v.folderId || null,
  isProcessing: v.status !== 'PROCESSING_FINISHED',
});

export const toFolder = (v: GQLFolder): Folder => ({
  id: v.id,
  name: v.name,
  parentId: v.parentId || null,
  updatedAt: v.updatedAt,
});

export const minFileSizeWarning = 1024 * 1024 * 100;
